if (!customElements.get('chase-infinite-scroll')) {
  customElements.define('chase-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('#ProductGridContainer');
      this.grid = this.container?.querySelector('ul.product-grid');
      this.controls = this.querySelector('[data-scroll-controls]');
      this.fallback = this.querySelector('[data-scroll-fallback]');
      this.button = this.querySelector('[data-scroll-more]');
      this.status = this.querySelector('[data-scroll-status]');
      if (!this.grid || !this.button || this.started) return;
      this.started = true;
      this.page = Number(this.dataset.page);
      this.nextUrl = this.dataset.nextUrl;
      this.loadedUrls = new Set();
      this.onClick = () => this.loadMore(true);
      this.button.addEventListener('click', this.onClick);
      this.controls.hidden = false;
      this.fallback.hidden = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
        }, { rootMargin: '0px 0px 500px 0px' });
      }
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.button?.removeEventListener('click', this.onClick);
      this.grid?.removeAttribute('aria-busy');
      this.started = false;
    }

    update() {
      const count = this.grid.children.length;
      this.status.textContent = this.dataset.progress.replace('[count]', count).replace('[total]', this.dataset.total);
      this.button.disabled = false;
      this.button.textContent = this.dataset.more;
      this.button.hidden = !this.nextUrl;
      if (!this.nextUrl) {
        this.observer?.disconnect();
        this.status.textContent += ' ' + this.dataset.complete;
      } else {
        this.observer?.unobserve(this);
        this.observer?.observe(this);
      }
    }

    async loadMore(manual = false) {
      if (this.loading || !this.nextUrl || !this.isConnected || this.container.querySelector('.collection.loading')) return;
      let url;
      try {
        url = new URL(this.nextUrl, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Unexpected pagination URL');
      } catch {
        this.showError();
        return;
      }
      url.searchParams.set('section_id', this.dataset.sectionId);
      if (this.loadedUrls.has(url.href)) return;
      this.loading = true;
      this.observer?.unobserve(this);
      this.button.disabled = true;
      this.button.textContent = this.dataset.loading;
      this.grid.setAttribute('aria-busy', 'true');
      const locationAtStart = window.location.pathname + window.location.search;
      this.controller = new AbortController();
      this.timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(url.href, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Pagination request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || locationAtStart !== window.location.pathname + window.location.search || this.container.querySelector('.collection.loading')) return;
        const next = doc.querySelector('chase-infinite-scroll');
        const fetchedGrid = doc.querySelector('#ProductGridContainer ul.product-grid');
        if (!next || !fetchedGrid || next.dataset.sectionId !== this.dataset.sectionId || Number(next.dataset.page) !== this.page + 1) throw new Error('Invalid pagination response');
        const seen = new Set([...this.grid.children].map((item) => item.dataset.chaseItemId));
        const cards = [...fetchedGrid.children].filter((item) => {
          const id = item.dataset.chaseItemId;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        if (!cards.length) throw new Error('No new results');
        const firstLink = cards[0].querySelector('.card__heading a, a');
        const fragment = document.createDocumentFragment();
        cards.forEach((card) => {
          card.classList.add('scroll-trigger--cancel');
          card.querySelectorAll('.scroll-trigger').forEach((item) => item.classList.add('scroll-trigger--cancel'));
          fragment.append(card);
        });
        this.grid.append(fragment);
        this.loadedUrls.add(url.href);
        this.page = Number(next.dataset.page);
        this.dataset.page = next.dataset.page;
        this.nextUrl = next.dataset.nextUrl;
        this.dataset.nextUrl = this.nextUrl;
        const nextFallback = next.querySelector('[data-scroll-fallback]');
        if (nextFallback) this.fallback.innerHTML = nextFallback.innerHTML;
        this.fallback.hidden = true;
        this.loading = false;
        this.update();
        if (manual && firstLink) firstLink.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('chase:products-appended', { bubbles: true, detail: { count: cards.length } }));
      } catch {
        if (this.isConnected && locationAtStart === window.location.pathname + window.location.search) this.showError();
      } finally {
        clearTimeout(this.timeout);
        this.loading = false;
        this.grid.removeAttribute('aria-busy');
      }
    }

    showError() {
      this.observer?.disconnect();
      this.status.textContent = this.dataset.error;
      this.button.textContent = this.dataset.retry;
      this.button.disabled = false;
      this.fallback.hidden = false;
    }
  });
}
